/**
 * Sentry Performance monitoring — browser + server config.
 *
 * SDK-agnostic factory helpers: they only construct integration objects and
 * option bags, so this module has no dependency on `@sentry/react`,
 * `@sentry/node`, or any Sentry package. When the SDK is actually installed,
 * pass the SDK's own `Sentry` namespace into the factory and spread the
 * result into `Sentry.init`.
 *
 * Why factories: Sentry ships different browser / server SDKs with their
 * own `browserTracingIntegration` / `httpIntegration` etc. Passing the
 * namespace in lets one config work for both without importing either at
 * build time (which would blow up SSR bundling for the browser SDK, and
 * vice versa).
 *
 * Real-user LCP/CLS/INP are captured automatically by `browserTracingIntegration`
 * as measurements on the pageload transaction — no extra code needed. Slow
 * request traces come from `httpIntegration` on the server plus distributed
 * tracing headers (`sentry-trace`, `baggage`) which the browser tracing
 * integration attaches to same-origin fetches by default.
 *
 * Wire in on the CLIENT:
 *
 *   import * as Sentry from "@sentry/react";
 *   import { sentryScrubbers } from "@/lib/sentry-scrubbers";
 *   import { sentryContext } from "@/lib/sentry-context";
 *   import { sentryReplayIntegration, sentryReplaySampleRates } from "@/lib/sentry-replay";
 *   import { sentryBrowserPerformance } from "@/lib/sentry-performance";
 *
 *   Sentry.init({
 *     dsn: import.meta.env.VITE_SENTRY_DSN,
 *     integrations: [
 *       sentryReplayIntegration(),
 *       ...sentryBrowserPerformance(Sentry).integrations,
 *     ],
 *     ...sentryBrowserPerformance(Sentry).options,
 *     ...sentryReplaySampleRates,
 *     ...sentryContext,
 *     ...sentryScrubbers,
 *   });
 *
 * Wire in on the SERVER (worker/SSR entry):
 *
 *   import * as Sentry from "@sentry/node"; // or the edge SDK you use
 *   import { sentryServerPerformance } from "@/lib/sentry-performance";
 *
 *   Sentry.init({
 *     dsn: process.env.SENTRY_DSN,
 *     integrations: sentryServerPerformance(Sentry).integrations,
 *     ...sentryServerPerformance(Sentry).options,
 *     ...sentryContext,
 *     ...sentryScrubbers,
 *   });
 */

// Loose SDK shape — we only need the integration factories that may exist.
type SentryLike = {
  browserTracingIntegration?: (opts?: unknown) => unknown;
  httpIntegration?: (opts?: unknown) => unknown;
  nodeContextIntegration?: (opts?: unknown) => unknown;
};

// Sample rates — env-tunable so we can dial down under launch load without a
// code deploy. Defaults are conservative: 100% in preview/dev so we see every
// trace while iterating, 20% in production for signal without a billing spike.
function num(v: unknown, fallback: number): number {
  const n = typeof v === "string" ? Number(v) : NaN;
  return Number.isFinite(n) && n >= 0 && n <= 1 ? n : fallback;
}

function isProd(): boolean {
  // Mirrors sentry-context.ts environment detection: any of these being
  // "production" flips prod-tuned defaults on.
  if (typeof process !== "undefined") {
    const env =
      process.env?.SENTRY_ENVIRONMENT ??
      process.env?.NODE_ENV ??
      process.env?.VITE_SENTRY_ENVIRONMENT;
    if (env === "production") return true;
  }
  try {
    const viteEnv = (import.meta as { env?: Record<string, string | undefined> })?.env;
    const v = viteEnv?.VITE_SENTRY_ENVIRONMENT ?? viteEnv?.MODE;
    if (v === "production") return true;
  } catch {
    /* not in a Vite context */
  }
  return false;
}

function readSampleRate(name: string, fallback: number): number {
  if (typeof process !== "undefined" && process.env?.[name] != null) {
    return num(process.env[name], fallback);
  }
  try {
    const viteEnv = (import.meta as { env?: Record<string, string | undefined> })?.env;
    const v = viteEnv?.[`VITE_${name}`];
    if (v != null) return num(v, fallback);
  } catch {
    /* not in a Vite context */
  }
  return fallback;
}

/**
 * Trace sample rate. Preview/dev: 1.0. Production: 0.2. Override with
 * SENTRY_TRACES_SAMPLE_RATE (server) or VITE_SENTRY_TRACES_SAMPLE_RATE (client).
 */
export function tracesSampleRate(): number {
  return readSampleRate("SENTRY_TRACES_SAMPLE_RATE", isProd() ? 0.2 : 1.0);
}

/**
 * Profiles sample rate (fraction of sampled traces that also get a profile).
 * Preview/dev: 1.0. Production: 0.1.
 */
export function profilesSampleRate(): number {
  return readSampleRate("SENTRY_PROFILES_SAMPLE_RATE", isProd() ? 0.1 : 1.0);
}

/**
 * Origins to attach distributed-tracing headers to. Same-origin is always
 * safe; add any first-party APIs the app calls cross-origin. Never include a
 * third-party origin — that leaks the Sentry trace ID to them.
 */
export const tracePropagationTargets: (string | RegExp)[] = [
  /^\//, // same-origin (relative URLs)
  /^https:\/\/([a-z0-9-]+\.)?recoverytrauma\.com/,
  /^https:\/\/id-preview--[a-z0-9-]+\.lovable\.app/,
  /^https:\/\/[a-z0-9-]+--[a-z0-9-]+\.lovable\.app/,
  /^https:\/\/recoverytrauma\.lovable\.app/,
];

export interface SentryPerformanceConfig {
  integrations: unknown[];
  options: {
    tracesSampleRate: number;
    profilesSampleRate?: number;
    tracePropagationTargets?: (string | RegExp)[];
  };
}

/**
 * Browser performance config. Enables `browserTracingIntegration`, which:
 *   - Creates a `pageload` transaction on first paint and reports LCP, CLS,
 *     INP, FCP, TTFB as measurements on it (real-user CWVs).
 *   - Creates `navigation` transactions on client-side route changes
 *     (works with any router since it hooks History API).
 *   - Attaches `sentry-trace` + `baggage` headers to same-origin fetches
 *     so server spans link into the same trace.
 */
export function sentryBrowserPerformance(Sentry: SentryLike): SentryPerformanceConfig {
  const integrations: unknown[] = [];
  if (typeof Sentry.browserTracingIntegration === "function") {
    integrations.push(
      Sentry.browserTracingIntegration({
        // Keep the defaults for pageload/navigation instrumentation. Idle
        // timeout is Sentry's default (1s) — plenty for CWV capture.
        traceFetch: true,
        traceXHR: true,
      }),
    );
  }
  return {
    integrations,
    options: {
      tracesSampleRate: tracesSampleRate(),
      tracePropagationTargets,
    },
  };
}

/**
 * Server performance config. Enables `httpIntegration` so inbound requests
 * become transactions and outbound `fetch`/`http` calls become child spans —
 * the two things you need to actually find slow endpoints in production.
 *
 * `nodeContextIntegration` (when present) adds runtime/OS tags so slow-request
 * traces are attributable to a specific deploy target.
 */
export function sentryServerPerformance(Sentry: SentryLike): SentryPerformanceConfig {
  const integrations: unknown[] = [];
  if (typeof Sentry.httpIntegration === "function") {
    integrations.push(
      Sentry.httpIntegration({
        // Trace both incoming and outgoing HTTP.
        tracing: true,
        // Do NOT auto-instrument breadcrumbs for outgoing requests here —
        // sentry-scrubbers already strips Authorization/Cookie, but keeping
        // the surface narrow means fewer places PII could leak in.
        breadcrumbs: true,
      }),
    );
  }
  if (typeof Sentry.nodeContextIntegration === "function") {
    integrations.push(Sentry.nodeContextIntegration());
  }
  return {
    integrations,
    options: {
      tracesSampleRate: tracesSampleRate(),
      profilesSampleRate: profilesSampleRate(),
      tracePropagationTargets,
    },
  };
}
